import { useEffect, useMemo, useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { CmsField, CmsGrid, CmsSection, cmsInput } from "@/components/admin/cms/cms-kit";
import { AToggle, FormRow } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import { Segmented } from "@/components/admin/segmented";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { useToast } from "@/components/ui/feedback";
import { env } from "@/config/env";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import {
	CHANNEL_LABEL,
	type CreateShortlinkInput,
	KIND_LABEL,
	type Shortlink,
	type ShortlinkChannel,
	type ShortlinkKind,
	shortLinksApi,
} from "@/lib/short-links/short-links-api";
import { useCreateShortLink } from "@/lib/short-links/short-links-hooks";

import { SmsPreview } from "./sms-preview";

type TargetMode = "product" | "category" | "deals" | "page" | "free";

const TARGET_MODES: Array<{ value: TargetMode; label: string }> = [
	{ value: "product", label: "Produit" },
	{ value: "category", label: "Catégorie" },
	{ value: "deals", label: "Bons plans" },
	{ value: "page", label: "Page" },
	{ value: "free", label: "URL libre" },
];

const KIND_BY_MODE: Record<TargetMode, ShortlinkKind> = {
	product: "product",
	category: "category",
	deals: "promotion",
	page: "cms",
	free: "custom",
};

const CHANNELS: ShortlinkChannel[] = [
	"sms",
	"whatsapp",
	"facebook",
	"instagram",
	"email",
	"print",
	"qr",
	"ads",
	"other",
];

interface Props {
	onClose: () => void;
	onCreated?: (link: Shortlink) => void;
}

/**
 * Création d'un lien court.
 *
 * Le formulaire guide vers une destination VALIDE plutôt que de laisser saisir
 * une adresse libre puis d'afficher une erreur : les trois quarts des liens
 * pointent sur un produit, une catégorie ou les bons plans, et une URL de
 * storefront mal formée (préfixe de langue oublié, mauvais domaine) est refusée
 * par l'API — autant construire l'adresse correcte dès le départ.
 */
export function CreateLinkModal({ onClose, onCreated }: Props) {
	const toast = useToast();
	const create = useCreateShortLink();

	const [mode, setMode] = useState<TargetMode>("product");
	const [slug, setSlug] = useState("");
	const [freeUrl, setFreeUrl] = useState("");
	const [label, setLabel] = useState("");
	const [channel, setChannel] = useState<ShortlinkChannel>("sms");
	const [campaign, setCampaign] = useState("");
	const [autoCode, setAutoCode] = useState(true);
	const [vanity, setVanity] = useState("");
	const [expiresAt, setExpiresAt] = useState("");
	const [maxClicks, setMaxClicks] = useState("");
	const [fallbackUrl, setFallbackUrl] = useState("");
	const [utm, setUtm] = useState({ source: "", medium: "", campaign: "", content: "", term: "" });

	const [codeState, setCodeState] = useState<{
		checking: boolean;
		available: boolean | null;
		reason?: string;
		normalized?: string;
	}>({ checking: false, available: null });

	const [created, setCreated] = useState<Shortlink | null>(null);

	const storefront = env.storefrontUrl.replace(/\/+$/, "");

	/** Destination composée à partir du mode choisi. */
	const targetUrl = useMemo(() => {
		const clean = slug.trim().replace(/^\/+|\/+$/g, "");
		switch (mode) {
			case "product":
				return clean ? `${storefront}/fr/produit/${clean}` : "";
			case "category":
				return clean ? `${storefront}/fr/categorie/${clean}` : "";
			case "deals":
				return `${storefront}/fr/bons-plans`;
			case "page":
				return clean ? `${storefront}/fr/${clean}` : "";
			case "free":
				return freeUrl.trim();
		}
	}, [mode, slug, freeUrl, storefront]);

	/** Vérification de disponibilité du code personnalisé, avec anti-rebond. */
	useEffect(() => {
		if (autoCode || vanity.trim().length < 3) {
			setCodeState({ checking: false, available: null });
			return;
		}
		setCodeState({ checking: true, available: null });
		const timer = setTimeout(() => {
			void shortLinksApi
				.checkCode(vanity.trim())
				.then((res) =>
					setCodeState({
						checking: false,
						available: res.available,
						reason: res.reason,
						normalized: res.code ?? undefined,
					}),
				)
				.catch(() => setCodeState({ checking: false, available: null }));
		}, 400);
		return () => clearTimeout(timer);
	}, [vanity, autoCode]);

	const canSubmit =
		targetUrl.length > 0 &&
		!create.isPending &&
		(autoCode || (vanity.trim().length >= 3 && codeState.available === true));

	const submit = async () => {
		const utmClean = Object.fromEntries(
			Object.entries(utm).filter(([, v]) => v.trim().length > 0),
		);

		const payload: CreateShortlinkInput = {
			targetUrl,
			kind: KIND_BY_MODE[mode],
			label: label.trim() || undefined,
			channel,
			campaign: campaign.trim() || undefined,
			code: autoCode ? undefined : vanity.trim(),
			utm: Object.keys(utmClean).length > 0 ? utmClean : undefined,
			expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
			maxClicks: maxClicks ? Number(maxClicks) : undefined,
			fallbackUrl: fallbackUrl.trim() || undefined,
		};

		try {
			const link = await create.mutateAsync(payload);
			setCreated(link);
			onCreated?.(link);
		} catch (e) {
			toast.error(extractErrorMessage(e, "Création du lien impossible."));
		}
	};

	// ── Panneau de succès ────────────────────────────────────────────────────
	if (created) {
		return (
			<Modal title="Lien court créé" onClose={onClose} width={560}>
				<div style={{ textAlign: "center", padding: "8px 0 4px" }}>
					<div
						className="mono"
						style={{
							fontSize: 22,
							fontWeight: 700,
							color: "var(--a-terracotta)",
							wordBreak: "break-all",
						}}
					>
						{created.smsForm}
					</div>
					<div className="muted" style={{ marginTop: 6, fontSize: 12.5 }}>
						{created.smsForm.length} caractères · {KIND_LABEL[created.kind]}
					</div>

					<div
						style={{
							display: "flex",
							gap: 8,
							justifyContent: "center",
							marginTop: 16,
							flexWrap: "wrap",
						}}
					>
						<AdminButton
							variant="primary"
							onClick={() => {
								void navigator.clipboard.writeText(created.smsForm);
								toast.success("Lien copié pour SMS.");
							}}
						>
							Copier pour SMS
						</AdminButton>
						<AdminButton
							onClick={() => {
								void navigator.clipboard.writeText(created.shortUrl);
								toast.success("Lien complet copié.");
							}}
						>
							Copier le lien complet
						</AdminButton>
					</div>
				</div>

				<ModalFooter>
					<AdminButton
						onClick={() => {
							setCreated(null);
							setSlug("");
							setFreeUrl("");
							setLabel("");
							setVanity("");
						}}
					>
						Créer un autre lien
					</AdminButton>
					<AdminButton variant="primary" onClick={onClose}>
						Terminé
					</AdminButton>
				</ModalFooter>
			</Modal>
		);
	}

	// ── Formulaire ───────────────────────────────────────────────────────────
	return (
		<Modal
			title="Nouveau lien court"
			subtitle="Un lien de 15 caractères, traçable, dont la destination reste modifiable après envoi."
			onClose={onClose}
			width={620}
		>
			<FormRow label="Destination" required>
				<Segmented options={TARGET_MODES} value={mode} onChange={setMode} />
			</FormRow>

			{mode === "product" || mode === "category" || mode === "page" ? (
				<FormRow
					label={
						mode === "product"
							? "Identifiant du produit (slug)"
							: mode === "category"
								? "Identifiant de la catégorie (slug)"
								: "Chemin de la page"
					}
					helper={targetUrl || "La destination s'affichera ici"}
					required
				>
					<input
						className="aform-i"
						value={slug}
						onChange={(e) => setSlug(e.target.value)}
						placeholder={mode === "product" ? "riz-parfume-5kg" : "electronique"}
					/>
				</FormRow>
			) : null}

			{mode === "deals" ? (
				<div
					style={{
						padding: "10px 12px",
						borderRadius: "var(--a-r-sm)",
						background: "var(--a-warning-soft)",
						color: "var(--a-warning)",
						fontSize: 12.5,
						marginBottom: 12,
					}}
				>
					La page « Bons plans » renvoie une erreur quand les ventes flash sont
					désactivées. Renseignez une URL de repli dans les paramètres avancés — sinon un
					lien imprimé tombera en 404 par simple bascule d'un réglage.
				</div>
			) : null}

			{mode === "free" ? (
				<FormRow
					label="Adresse complète"
					helper="En https, sur un domaine autorisé. Le préfixe de langue (/fr) est ajouté si besoin."
					required
				>
					<input
						className="aform-i"
						value={freeUrl}
						onChange={(e) => setFreeUrl(e.target.value)}
						placeholder="https://akwaba.cm/fr/…"
					/>
				</FormRow>
			) : null}

			<FormRow label="Libellé interne" helper="Pour vous retrouver dans la liste.">
				<input
					className="aform-i"
					value={label}
					onChange={(e) => setLabel(e.target.value)}
					maxLength={120}
					placeholder="Campagne rentrée — riz 5 kg"
				/>
			</FormRow>

			<FormRow label="Code personnalisé">
				<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
					<AToggle on={autoCode} onChange={setAutoCode} />
					<span className="muted" style={{ fontSize: 12.5 }}>
						Générer automatiquement
					</span>
				</div>
			</FormRow>

			{!autoCode ? (
				<FormRow
					label="Votre code"
					helper={
						codeState.checking
							? "Vérification…"
							: codeState.available === true
								? `Disponible : ${codeState.normalized}`
								: codeState.available === false
									? (codeState.reason ?? "Indisponible")
									: "Lettres, chiffres 1-9 et tirets. Trois caractères minimum."
					}
				>
					<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
						<span className="mono muted" style={{ fontSize: 12.5 }}>
							{env.shortlinkUrl.replace(/^https?:\/\//, "")}/
						</span>
						<input
							className="aform-i mono"
							value={vanity}
							onChange={(e) => setVanity(e.target.value)}
							maxLength={32}
							placeholder="rentree"
							style={{ flex: 1 }}
						/>
						{codeState.available === true ? (
							<AdminIcon n="check" s={16} c="var(--a-success)" />
						) : codeState.available === false ? (
							<AdminIcon n="close" s={16} c="var(--a-danger)" />
						) : null}
					</div>
				</FormRow>
			) : null}

			<CmsGrid min={220}>
				<CmsField label="Canal">
					<select
						style={cmsInput}
						value={channel}
						onChange={(e) => setChannel(e.target.value as ShortlinkChannel)}
					>
						{CHANNELS.map((c) => (
							<option key={c} value={c}>
								{CHANNEL_LABEL[c]}
							</option>
						))}
					</select>
				</CmsField>
				<CmsField label="Campagne">
					<input
						style={cmsInput}
						value={campaign}
						onChange={(e) => setCampaign(e.target.value)}
						maxLength={64}
						placeholder="rentree-2026"
					/>
				</CmsField>
			</CmsGrid>

			<div style={{ margin: "14px 0" }}>
				<SmsPreview shortForm={`${env.shortlinkUrl.replace(/^https?:\/\//, "")}/a3k9mz7`} longUrl={targetUrl} />
			</div>

			<CmsSection title="Paramètres avancés" defaultOpen={false}>
				<CmsGrid min={220}>
					<CmsField label="Expiration" hint="Après cette date, le lien cesse de rediriger.">
						<input
							style={cmsInput}
							type="datetime-local"
							value={expiresAt}
							onChange={(e) => setExpiresAt(e.target.value)}
						/>
					</CmsField>
					<CmsField label="Quota de clics" hint="Clics humains seulement.">
						<input
							style={cmsInput}
							type="number"
							min={1}
							value={maxClicks}
							onChange={(e) => setMaxClicks(e.target.value)}
						/>
					</CmsField>
				</CmsGrid>

				<CmsField
					label="URL de repli"
					hint="Servie à la place de la page d'expiration quand le lien n'est plus actif."
				>
					<input
						style={cmsInput}
						value={fallbackUrl}
						onChange={(e) => setFallbackUrl(e.target.value)}
						placeholder="https://akwaba.cm/fr"
					/>
				</CmsField>

				<CmsGrid min={180}>
					{(["source", "medium", "campaign", "content", "term"] as const).map((k) => (
						<CmsField key={k} label={`utm_${k}`}>
							<input
								style={cmsInput}
								value={utm[k]}
								onChange={(e) => setUtm({ ...utm, [k]: e.target.value })}
								maxLength={64}
							/>
						</CmsField>
					))}
				</CmsGrid>
			</CmsSection>

			<ModalFooter>
				<AdminButton onClick={onClose}>Annuler</AdminButton>
				<AdminButton variant="primary" onClick={() => void submit()} disabled={!canSubmit}>
					{create.isPending ? "Création…" : "Créer le lien"}
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}
